import makeUUID from "@shared/utils/helpers/makeUUID"
import { pick } from "rambda"
import { useCallback, useMemo } from "react"
import { ApiError } from "@shared/primitive-components"
import { UUID } from "io-ts-types/lib/UUID"
import { Variables, useFilterVehicles, FilterVehicle } from "../../../../../../api/vehicles/filter"
import { AddedFleetEntry } from "../../../../../../api/fleet"
import { DropdownFieldComponent, DropdownFieldComponentProps } from "./DropdownFieldComponent"

type Props = Omit<DropdownFieldComponentProps, "dropdownValues" | "disabled" | "onChange"> & {
  entry: AddedFleetEntry
  onConfirmed: (vehicle: FilterVehicle) => void
}

export const prerequisiteFields = [`make`, `model`, `fuelType`] as (keyof Variables)[]

export const DropdownConfirmComponentWithDataLoad = ({ entry, onConfirmed, ...props }: Props) => {
  const [res] = useFilterVehicles({
    variables: pick(prerequisiteFields, entry),
  })

  const dropdownValueMap = useMemo(() => {
    const valueMap: { [key: UUID]: string } = {}
    const vehicles = res.result?.data?.vehicles || []
    vehicles
      .filter(
        (it) =>
          (!entry.make || it.make === entry.make) &&
          (!entry.model || it.model === entry.model) &&
          (!entry.trim || it.trim === entry.trim) &&
          (!entry.nominalStartYear || it.nominalStartYear === entry.nominalStartYear)
      )
      .forEach((it) => {
        valueMap[it.id] = `${it.make} ${it.model} ${it.trim || ``} ${it.nominalStartYear || ``}`
      })
    return valueMap
  }, [res.result?.data?.vehicles, entry])

  const onChange = useCallback(
    (value: string) => {
      const vehicleId = Object.keys(dropdownValueMap).find((id) => dropdownValueMap[makeUUID(id)] === value)
      if (!vehicleId) return
      const vehicle = (res.result?.data?.vehicles || []).find((it) => it.id === makeUUID(vehicleId))
      if (!vehicle) return
      onConfirmed(vehicle)
    },
    [res.result?.data?.vehicles, dropdownValueMap, onConfirmed]
  )

  if (res.result?.tag === `err`) {
    return <ApiError error={res.result.error.type} />
  }

  return (
    <DropdownFieldComponent
      {...props}
      onChange={onChange}
      dropdownValues={Object.values(dropdownValueMap)}
      disabled={res.loading || !res?.result}
    />
  )
}
